import { client } from "./supabase";
import { exigirFechaValida } from "./validarFechaDocumento";

export const getActivos = async (pais, filters = {}) => {
  let query = client
    .from("activos")
    .select(
      `
      *,
      proveedor:proveedores(id, nombre, alias),
      factura_sii:facturas_sii_recibidas(id, folio, razon_social_emisor)
    `
    )
    .eq("pais", pais)
    .eq("eliminado", false)
    .order("fecha_compra", { ascending: false });

  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.destino_tipo) query = query.eq("destino_tipo", filters.destino_tipo);
  if (filters.destino_id) query = query.eq("destino_id", filters.destino_id);
  if (filters.proyecto_nombre) query = query.eq("proyecto_nombre", filters.proyecto_nombre);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// F1 (B.2b, 2026-09-26, "Futuros: reagendar" Fase 5): `fecha_planificada` exige `modo` explícito
// (MODOS_VALIDACION) en `opciones` — el candado vive DENTRO del writer, no en la disciplina de cada
// pantalla que lo llama (10.190 aplicado al frontend). Sin modo, `exigirFechaValida` lanza (10.177).
export const crearActivo = async (activo, opciones = {}) => {
  exigirFechaValida(activo, "fecha_planificada", opciones);
  const { data, error } = await client
    .from("activos")
    .insert({ ...activo, eliminado: false })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const actualizarActivo = async (id, cambios, opciones = {}) => {
  exigirFechaValida(cambios, "fecha_planificada", opciones);
  const { data, error } = await client
    .from("activos")
    .update({ ...cambios, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const eliminarActivo = async (id) => {
  const { error } = await client
    .from("activos")
    .update({ eliminado: true, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

export const getActivosPlanificados = async (pais) => getActivos(pais, { estado: "planificado" });

export const getActivosPorDestino = async (pais, destino_tipo, destino_id) =>
  getActivos(pais, { destino_tipo, destino_id });

export const getActivosPorProyecto = async (pais, proyecto_nombre) => getActivos(pais, { proyecto_nombre });

// ─── Fase 1a (proyección/factura/pago, 2026-10-04) — vínculo factura↔activo (Vínculo A, Camino A) ──
// Molde: src/utils/costosProyecto.js (getFacturasParaVincularCosto/vincularDocumentoCosto de
// Cartelería Fase 4), adaptado a Edificios. Único escritor de `asociaciones_factura_activo`
// junto con la reversa — ver supabase/migrations/20261004h_asociaciones_factura_activo.sql.

export const getActivoPorId = async (id) => {
  const { data, error } = await client
    .from("activos")
    .select("*, proveedor:proveedores(id, nombre, alias)")
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
};

// Facturas candidatas para vincular a UN activo planificado: del mismo proveedor/país, SII
// recibida (nunca externa — una externa no genera activo, mismo criterio OPEX de Cartelería),
// excluyendo NC (tipo_dte=61) y las que ya tienen una asociación activa (evita un dead-end: la RPC
// la rechazaría igual, esto solo prefiltra el picker, 10.1 — la RPC vuelve a validar todo).
export const getFacturasParaVincularActivo = async ({ pais, proveedorId }) => {
  if (!pais || !proveedorId) return [];
  const { data: activas, error: eActivas } = await client
    .from("asociaciones_factura_activo")
    .select("factura_sii_recibida_id")
    .eq("estado", "activa");
  if (eActivas) throw eActivas;
  const idsConAsociacion = (activas || []).map((a) => a.factura_sii_recibida_id);

  let query = client
    .from("facturas_sii_recibidas")
    .select("id, folio, fecha_emision, monto_neto")
    .eq("pais", pais)
    .eq("proveedor_id", proveedorId)
    .neq("tipo_dte", 61)
    .order("fecha_emision", { ascending: false });
  if (idsConAsociacion.length > 0) query = query.not("id", "in", `(${idsConAsociacion.join(",")})`);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((f) => ({
    id: f.id,
    label: `Folio ${f.folio}`,
    fechaEmision: f.fecha_emision,
    montoNeto: Number(f.monto_neto),
  }));
};

// Activos candidatos para vincular a UNA factura ya conocida: planificados O ejecutado_sin_factura
// (Fase 2a, 2026-10-05: decisión de Ignacio habilita ligar a mano los "soportes" reales 429/439/505
// — la "antena" 428 queda fuera por el tema de stock/lotes, ver ROADMAP, pero esta lista no filtra
// por descripción, solo por estado), sin factura, no eliminados, del mismo país, y del mismo
// proveedor O todavía sin proveedor asignado (la RPC lo setea con el de la factura — mismo
// criterio que vincular_factura_activo paso 4).
export const getActivosParaVincularFactura = async ({ pais, proveedorId }) => {
  if (!pais) return [];
  const { data, error } = await client
    .from("activos")
    .select(
      "id, descripcion, costo_unitario, moneda, fecha_planificada, proveedor_id, estado, destino_tipo, destino_id"
    )
    .eq("pais", pais)
    .in("estado", ["planificado", "ejecutado_sin_factura"])
    .is("factura_sii_recibida_id", null)
    .eq("eliminado", false)
    .or(proveedorId ? `proveedor_id.is.null,proveedor_id.eq.${proveedorId}` : "proveedor_id.is.null")
    .order("fecha_planificada", { ascending: true });
  if (error) throw error;
  const activos = data || [];
  if (activos.length === 0) return [];

  // Nombre del edificio (destino_tipo='edificio' — esta rama es SOLO Edificios, Fase 1a).
  // destino_id es polimórfico (sin FK directa a embeber, molde GestionActivos) — UNA sola query
  // de lote para todos los edificios involucrados (10.234).
  const edificioIds = [...new Set(activos.filter((a) => a.destino_tipo === "edificio").map((a) => a.destino_id))];
  let edificiosMap = new Map();
  if (edificioIds.length > 0) {
    const { data: edificios, error: eEdif } = await client
      .from("edificios")
      .select("id, edificio")
      .in("id", edificioIds);
    if (eEdif) throw eEdif;
    edificiosMap = new Map((edificios || []).map((e) => [e.id, e.edificio]));
  }

  return activos.map((a) => ({
    ...a,
    edificioNombre:
      a.destino_tipo === "edificio" ? edificiosMap.get(a.destino_id) || `Edificio #${a.destino_id}` : null,
  }));
};

// RPC `vincular_factura_activo` (único alta). `activoIds` es SIEMPRE un array (1..N).
export const vincularFacturaActivo = async ({ facturaId, activoIds, usuarioId = null }) => {
  const { data, error } = await client.rpc("vincular_factura_activo", {
    p_factura_id: facturaId,
    p_activo_ids: activoIds,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};

// Facturas con asociación ACTIVA a 1+ activos (molde `getFacturasConAsociacionCapex`, 10.1) — Map
// factura_sii_recibida_id → asociacion_id (`desvincular_factura_activo` necesita el id de la
// ASOCIACIÓN, no el de la factura). Fase 2d: lo usa `ComprasTab.js` para decidir si el kebab
// ofrece "Corresponde a una proyección" o "Desvincular de la proyección".
export const getFacturasConAsociacionActivo = async (pais) => {
  const { data, error } = await client
    .from("asociaciones_factura_activo")
    .select("id, factura_sii_recibida_id")
    .eq("estado", "activa")
    .eq("pais", pais);
  if (error) throw error;
  return new Map((data || []).map((r) => [r.factura_sii_recibida_id, r.id]));
};

// RPC `desvincular_factura_activo` (reversa por evento — nunca borra la fila).
export const desvincularFacturaActivo = async ({ asociacionId, usuarioId = null }) => {
  const { data, error } = await client.rpc("desvincular_factura_activo", {
    p_asociacion_id: asociacionId,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};
